import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  effect,
  untracked,
  WritableSignal,
  signal,
} from '@angular/core';
import { Router, ActivatedRoute, UrlTree } from '@angular/router';
import { Location } from '@angular/common';
import { Observable } from 'rxjs';
import { Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { LazyLoadEvent } from 'primeng/api';
import {
  ActiveFilterService,
  BreadcrumbService,
  ColumnDefinitionService,
  MetricsGridConfig,
  ClientProfileService,
  GridTableConfiguredExpandableComponent,
  FormatService,
  GridSortingService,
} from '../core';
import { DownloadsService } from '../downloads';
import { EquipmentService } from '../equipment';
import { HeaderService } from '../header';
import { SalesRepBaseComponent } from './sales-rep-base.component';
import { SelectedSalesRepService } from './selected-sales-rep.service';
import { PageOptionsNg, SortOptionsNg, SortOptions } from '../models';
import { AuthenticationService } from '../core/authentication/authentication.service';
import { FilterProfileUtilsService } from '@/app/filter/profiles/filter-profile-utils.service';
import { SavedViewsService } from '@/app/modules/saved-views/saved-views.service';
import { TranslateService } from '@ngx-translate/core';
import { customersSavedViewDeserializer as savedViewReducer } from '@/app/customer/customer-utils';
import { createGridSerializer } from '@/app/modules/saved-views/saved-views-serializer';
import { FeatureFlagService } from '@/app/core/feature-flags/feature-flag.service';

@Component({
  selector: 'rdo-product-type-customers',
  template: `
    <rdo-overlay [enabled]="loading" />
    <ng-container *featureFlag="'flagSavedViews'">
      <rdo-saved-views
        [gridName]="gridName"
        [pageName]="
          translateService.instant('main.core.common.counts.customers.plural')
        "
      />
    </ng-container>
    @if (data && totalCount > 0) {
      <rdo-grid-table-expanded-ng
        #productTypesGrid
        [name]="gridName"
        [dataKey]="dataKey"
        [gridConfig]="gridConfig()"
        [rowExpansion]="gridConfig().rowExpansion"
        [pagedData]="data"
        [frozenRows]="frozenRows"
        [sorting]="sorting"
        [(paging)]="paging"
        (pageOnChange)="changePage($event)"
        (lazyLoadOnChange)="changeLazyLoad($event)"
        [totalCount]="totalCount"
        [frozenWidth]="frozenWidth"
        [showColumnSelector]="true"
        (downloadExcelClick)="exportExcel($event)"
        [alwaysShowPager]="false"
      >
      </rdo-grid-table-expanded-ng>
    }
  `,
  standalone: false,
})
export class SalesRepProductTypesComponent
  extends SalesRepBaseComponent
  implements OnInit, OnDestroy
{
  @ViewChild('productTypesGrid')
  productTypesGrid: GridTableConfiguredExpandableComponent;
  loading = true;
  gridName = 'SALESREP_PRODUCTTYPES_GRID';
  data: any;
  salesRepId: string;
  gridConfig: WritableSignal<MetricsGridConfig> = signal(null);
  frozenRows: any;
  paging = new PageOptionsNg();
  sorting: SortOptionsNg;
  totalCount = 0;
  frozenWidth: string;
  dataKey = 'ProductType';
  private clientProfileServiceSubscription: Subscription;

  constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private columnService: ColumnDefinitionService,
    private filterService: ActiveFilterService,
    private headerService: HeaderService,
    breadcrumbService: BreadcrumbService,
    selectedSalesRepService: SelectedSalesRepService,
    private equipmentService: EquipmentService,
    private downloadsService: DownloadsService,
    private clientProfileService: ClientProfileService,
    private formatService: FormatService,
    private gridSortingService: GridSortingService,
    private filterProfileUtilsService: FilterProfileUtilsService,
    private savedViewsService: SavedViewsService,
    public translateService: TranslateService,
    protected featureFlagService: FeatureFlagService
  ) {
    super(selectedSalesRepService, breadcrumbService);

    effect(() => {
      const areSavedViewsEnabled =
        this.featureFlagService.isActive('flagSavedViews');
      if (!areSavedViewsEnabled) {
        return;
      }
      const selectedView = this.savedViewsService.getSelectedView();
      const viewName = selectedView?.name ?? null;

      const lastApplied =
        this.savedViewsService.previouslySelectedViewsStack()[0] ?? null;

      if (viewName === lastApplied) {
        return;
      }

      const hasGridConfig = untracked(() => !!this.gridConfig());

      if (selectedView && hasGridConfig) {
        // Call handler in untracked context to avoid tracking any signal reads inside
        untracked(async () => {
          const currentConfig = this.gridConfig();

          // Define a callback to extract visible column names for logging
          const getVisibleColumns = (config: MetricsGridConfig) => {
            return (
              config?.groups
                ?.filter((g) => g.visible)
                .map((g) => g.columnSelectorTitle) || []
            );
          };

          const newConfig = await this.savedViewsService.handleSavedViewChange(
            selectedView,
            currentConfig,
            savedViewReducer,
            getVisibleColumns
          );

          // Update the signal - this will automatically trigger change detection
          this.gridConfig.set(newConfig.config);

          // Signal that the full saved view application is complete
          this.savedViewsService.notifyViewApplied();
        });
      }
    });
  }

  ngOnInit() {
    const areSavedViewsEnabled =
      this.featureFlagService.isActive('flagSavedViews');
    if (areSavedViewsEnabled) {
      this.savedViewsService.initGrid(this.gridName, this.getSerializer());
    }
    this.gridSortingService.setGridName(this.gridName);
    this.sorting = this.gridSortingService.getSortOptionsNg();

    const component = this; // eslint-disable-line @typescript-eslint/no-this-alias
    let salesRepId = component.salesRepId;
    component.subscriptions.push(
      component.route.parent.params.subscribe(async (params) => {
        const areSavedViewsEnabled =
          this.featureFlagService.isActive('flagSavedViews');
        salesRepId = params.salesRep;
        if (component.salesRepId !== salesRepId) {
          component.salesRepId = salesRepId;
          component.configureGrid();
          if (areSavedViewsEnabled) {
            await component.savedViewsService.load();
          }
          component.load();
        }
      })
    );
    component.subscriptions.push(
      component.selectedSalesRepService.salesRepChange.subscribe((s) => {
        component.salesRepId = s.SalesRepId.toString();
        salesRepId = s.SalesRepId.toString();
        this.paging.page = 1;
        if (component.productTypesGrid) {
          component.productTypesGrid.resetPaginator();
        }
        component.load();
      })
    );
    component.subscriptions.push(
      component.filterService.filterChange.subscribe(() => {
        // If saved views is currently applying a view or its filters, skip —
        // the saved view effect will handle configureGrid + load.
        if (this.savedViewsService.isApplying()) {
          return;
        }
        component.configureGrid();
        component.load();
      })
    );
    component.subscriptions.push(
      component.authenticationService.userInfoView.subscribe((userInfo) => {
        if (component.gridConfig()) {
          const config = this.gridConfig();
          config.enableExcelExport = userInfo.HasClientAccessToExportData;
          this.gridConfig.set(config);
        }
      })
    );
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  protected getProductTypeHeading(): string {
    return this.filterProfileUtilsService.getProductTypesHeader().singular();
  }

  load = () => {
    const sorting = this.getSorting();

    this.loading = true;
    this.equipmentService
      .getSalesRepProductTypes(this.salesRepId, this.paging, sorting)
      .subscribe((result) => {
        this.loading = false;
        if (result.Items && result.Items.length > 0) {
          this.data = result.Items;
        } else {
          this.data = [];
        }
        this.totalCount = result.TotalCount;
      });
    this.updateBreadcrumbs([
      {
        title: 'main.core.common.counts.product_types.plural',
        class: 'active',
      },
    ]);
  };

  changePage = (event: any) => {
    if (event.rows !== this.paging.pageSize) {
      this.paging.pageSize = event.rows;
    }
    this.paging.page = event.first < 1 ? 1 : event.first / event.rows + 1;
    this.load();
  };

  changeLazyLoad = (event: LazyLoadEvent) => {
    if (
      this.sorting.sortField === event.sortField &&
      this.sorting.sortOrder === event.sortOrder
    ) {
      return;
    }

    this.gridSortingService.setSortOption(
      event.sortField,
      event.sortOrder === -1
    );

    this.sorting.sortField =
      event.sortField || this.gridSortingService.defaultSortColumn;
    this.sorting.sortOrder = event.sortOrder || -1;
    this.load();
  };

  exportExcel = (args: any) => {
    this.loading = true;
    const sorting = this.getSorting();
    let salesRep = '';
    this.selectedSalesRepService.SalesRep.subscribe(
      (result) => (salesRep = result.SalesRepName)
    );
    const translatedConfig = this.gridConfig().cloneAndTranslate((text) =>
      this.formatService.translateAndFormat(text, false)
    );
    this.subscriptions.push(
      this.equipmentService
        .getSalesRepProductTypesDownload(
          salesRep,
          this.salesRepId,
          sorting,
          translatedConfig
        )
        .subscribe((blob) => {
          this.loading = false;
          this.downloadsService.saveExcelBlob(blob);
        })
    );
  };

  private handleTransactionCountClick = (record: any, parent: any): boolean => {
    this.updateBreadcrumbs([
      {
        title: parent.Description,
        class: 'active',
      },
      {
        title: record.CustomerName,
        class: 'active',
      },
    ]);

    const urlTree = this.getTransactionUrlTree(record, parent);
    this.router.navigateByUrl(urlTree);
    return false;
  };

  private getTransactionUrlTree = (record: any, parent: any): UrlTree => {
    return this.router.createUrlTree(
      [
        'transactions',
        {
          productType: parent.ProductType,
          customer: record.CustomerId,
        },
      ],
      {
        relativeTo: this.route.parent,
      }
    );
  };

  private generateTransactionsLink = (record: any, parent: any): string => {
    const urlTree = this.getTransactionUrlTree(record, parent);
    return this.location.prepareExternalUrl(urlTree.toString());
  };

  private getSorting = (): SortOptions => {
    this.sorting.sortField = this.gridSortingService.getSortFieldOrDefault();
    this.sorting.sortOrder =
      this.gridSortingService.getSortDirectionOrDefault();
    return new SortOptions(
      this.sorting.sortField,
      !(this.sorting.sortOrder === 1)
    );
  };

  private configureGrid = () => {
    this.clientProfileServiceSubscription = this.clientProfileService
      .getClientAttributes()
      .subscribe((attributes) => {
        if (this.clientProfileServiceSubscription) {
          this.clientProfileServiceSubscription.unsubscribe();
        }
        let calculatedProductTypeLength = attributes.MaxProdTypeLen * 7.25;
        calculatedProductTypeLength =
          calculatedProductTypeLength < 175 ? 175 : calculatedProductTypeLength;
        this.frozenWidth = `${calculatedProductTypeLength + 175 + 110}px`;
        const config = new MetricsGridConfig(
          [
            {
              title: '',
              visible: true,
              locked: true,
              columnSelectorTitle: this.getProductTypeHeading(),
              columns: [
                {
                  title: this.getProductTypeHeading(),
                  field: 'Description',
                  sortColumn: 'Description',
                  width: 100,
                  maxWidth: 500,
                  minWidth: 115,
                  autoWidth: true,
                  headerStyle: {
                    'text-align': 'left',
                  },
                  cellStyle: {
                    'text-align': 'left',
                    'text-transform': 'uppercase',
                  },
                  childConfig: {
                    field: '',
                  },
                },
                this.columnService.CustomerCount(this.columnService.Customer()),
                this.columnService.TransactionCount(
                  this.columnService.TransactionCount(
                    null,
                    (v, r, p) => this.generateTransactionsLink(r, p),
                    (v, r, p) => this.handleTransactionCountClick(r, p)
                  )
                ),
              ],
            },
            ...this.columnService.StandardMetricSetColumnGroups(),
          ],
          (p) => this.loadChildren(p),
          true
        );
        this.gridConfig.set(config);
      });
  };

  private loadChildren = (parent: any): Observable<Array<any>> => {
    const sorting = this.getSorting();
    const useRentedAsProductType =
      this.filterService.getCurrentFilter().useRentedAsProductTypes === 1;
    return this.equipmentService
      .getSalesRepProductTypesCustomers(
        {
          salesRepId: this.salesRepId,
          productType: parent.ProductType,
          useRentedAsProductType,
        },
        sorting
      )
      .pipe(
        map((items) => {
          return items.map(this.convert);
        })
      );
  };

  private convert = (item: any): any => {
    const clone = (<any>Object).assign({}, item);
    clone.Category = null;
    clone.CustomerCount = item.CustomerName;
    return clone;
  };

  private getSerializer = createGridSerializer({
    gridName: this.gridName,
    gridConfig: this.gridConfig,
    gridSortingService: this.gridSortingService,
  });
}
